import { useState, useEffect } from 'react'
import { CheckCircle, Package, Award, Target, Eye } from 'lucide-react'
import CustomersEntity from '@/entities/api/customers/customer'
import Layout from '@/components/layout/layout'
import { Link } from 'react-router-dom'
import Banner from '/img/banner.jpg'
import Banner2 from '/img/banner_web_2.png'

const AboutUs = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const [isVisible, setIsVisible] = useState(false)

	useEffect(() => {
		setIsVisible(true)
	}, [])

	const metrics = [
		{
			icon: <CheckCircle className='w-8 h-8' />,
			number: '15,000+',
			label: 'Clientes Satisfechos',
		},
		{
			icon: <Package className='w-8 h-8' />,
			number: '500+',
			label: 'Productos Premium',
		},
		{
			icon: <Award className='w-8 h-8' />,
			number: '8+',
			label: 'Años de Experiencia',
		},
	]

	return (
		<Layout customer={customer} token={token}>
			<div className='min-h-screen bg-white'>
				<div className='max-w-full mx-auto flex flex-col items-center '>
					<section className='flex w-full flex-col xl:flex-row min-h-screen items-center px-6 xl:px-24 py-12 xl:py-20'>
						<div
							className={`w-full xl:w-1/2 flex flex-col items-center justify-center p-8 lg:p-12 xl:p-20 transform transition-all duration-1200 ease-out ${
								isVisible
									? 'translate-y-0 opacity-100'
									: 'translate-y-16 opacity-0'
							}`}
						>
							<div className='text-center xl:text-left max-w-2xl'>
								<h1 className='text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black text-gray-900 leading-none mb-6 tracking-tight whitespace-nowrap'>
									Conoce la comunidad
								</h1>

								<div className='relative inline-block mb-12 text-center'>
									<h1 className='relative z-10 font-black tracking-tight leading-tight'>
										<span className='flex items-center justify-center xl:justify-start gap-3'>
											<img
												src='/logo.png'
												alt='SportCenter Logo'
												className='w-10 h-10 md:w-12 md:h-12 object-contain'
											/>
											<span className='block text-4xl md:text-4xl lg:text-5xl xl:text-7xl text-red-600'>
												SportCenter
											</span>
										</span>

										<span className='block text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-black mt-1'>
											Shop
										</span>
									</h1>
									<div className='absolute -bottom-3 left-1/2 -translate-x-1/2 w-[90%] h-5 bg-gradient-to-r from-red-100 to-red-200 -z-10 transform -skew-x-12 rounded-sm'></div>
								</div>

								<div className='space-y-7 text-gray-700 text-base md:text-lg xl:text-xl font-medium leading-relaxed'>
									<p className='text-gray-800'>
										Somos un centro de entrenamiento que inspira y transforma
										vidas, ayudando a miles de personas a descubrir su mejor
										versión. Ahora damos un gran paso al mundo digital con
										nuestra tienda, donde encontrarás planes personalizados y
										productos de calidad a precios justos. Creemos en el fitness
										no solo como ejercicio, sino como un estilo de vida que
										regala salud, energía y bienestar.
									</p>
									<p className='text-gray-700'>
										Nuestra misión es proporcionar productos de la más alta
										calidad, respaldados por ciencia y experiencia, junto con el
										acompañamiento personalizado que necesitas para lograr tus
										objetivos de forma sostenible y saludable.
									</p>
								</div>

								<div className='mt-12 flex flex-wrap gap-5 justify-center xl:justify-start'>
									<Link
										to='/'
										className='border-2 cursor-pointer border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:shadow-lg'
									>
										Ver Productos
									</Link>
								</div>
							</div>
						</div>

						<div
							className={`w-full xl:w-1/2 p-6 xl:p-0 transform transition-all duration-1200 delay-300 ease-out ${
								isVisible
									? 'translate-x-0 opacity-100'
									: 'translate-x-16 opacity-0'
							}`}
						>
							<div className='relative h-[450px] md:h-[550px] lg:h-[650px] xl:h-[700px] overflow-hidden rounded-3xl shadow-2xl group'>
								<img
									src={Banner}
									alt='SportCenter Shop Team'
									className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out'
								/>
								<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>
								<div className='absolute bottom-8 left-8 text-white'>
									<div className='bg-red-600 hover:bg-red-700 px-6 py-3 rounded-full text-sm font-bold transition-colors duration-300 cursor-pointer shadow-lg'>
										Nuestro equipo
									</div>
								</div>
							</div>
						</div>
					</section>

					<section className='py-24 w-full px-6 bg-gradient-to-r from-gray-50 to-white'>
						<div className='max-w-7xl mx-auto'>
							<div
								className={`flex flex-col xl:flex-row items-center gap-16 transform transition-all duration-1200 ease-out ${
									isVisible
										? 'translate-y-0 opacity-100'
										: 'translate-y-16 opacity-0'
								}`}
							>
								<div className='w-full xl:w-1/2'>
									<div className='relative h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden rounded-3xl shadow-2xl group'>
										<img
											src={Banner2}
											alt='Nuestra Misión'
											className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out'
										/>
										<div className='absolute inset-0 bg-gradient-to-br from-red-600/20 via-transparent to-black/30'></div>
										<div className='absolute top-8 right-8'>
											<div className='w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center'>
												<Target className='w-8 h-8 text-white' />
											</div>
										</div>
									</div>
								</div>

								<div className='w-full xl:w-1/2'>
									<div className='text-center xl:text-left'>
										<div className='inline-flex items-center gap-3 bg-red-50 px-6 py-3 rounded-full mb-8'>
											<Target className='w-6 h-6 text-red-600' />
											<span className='text-red-600 font-bold text-lg'>
												Nuestra Misión
											</span>
										</div>

										<h2 className='text-3xl md:text-4xl xl:text-5xl font-black text-gray-900 leading-tight mb-8 tracking-tight'>
											Transformar vidas a través del
											<span className='text-red-600 block'>
												bienestar integral
											</span>
										</h2>

										<div className='space-y-6 text-gray-700 text-base md:text-lg xl:text-xl font-medium leading-relaxed'>
											<p className='text-gray-800'>
												En SportCenter Shop, nuestra misión es empoderar a cada
												persona para que alcance su máximo potencial físico y
												mental. Proporcionamos productos de calidad superior,
												respaldados por ciencia y experiencia.
											</p>
											<p>
												Creemos en el acompañamiento personalizado y en crear
												una comunidad donde cada objetivo se convierte en una
												realidad alcanzable, sostenible y saludable.
											</p>
										</div>

										<div className='mt-10 flex flex-wrap gap-4'>
											<div className='bg-white px-6 py-3 rounded-full border-2 border-gray-100 shadow-sm'>
												<span className='text-gray-700 font-semibold'>
													Calidad Superior
												</span>
											</div>
											<div className='bg-white px-6 py-3 rounded-full border-2 border-gray-100 shadow-sm'>
												<span className='text-gray-700 font-semibold'>
													Acompañamiento Personal
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>

					<section className='py-24 w-full px-6 bg-gradient-to-l from-gray-50 to-white'>
						<div className='max-w-7xl mx-auto'>
							<div
								className={`flex flex-col xl:flex-row-reverse items-center gap-16 transform transition-all duration-1200 ease-out ${
									isVisible
										? 'translate-y-0 opacity-100'
										: 'translate-y-16 opacity-0'
								}`}
							>
								<div className='w-full xl:w-1/2'>
									<div className='relative h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden rounded-3xl shadow-2xl group'>
										<img
											src='/img/banner_web_3.JPG'
											alt='Nuestra Visión'
											className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out'
										/>
										<div className='absolute inset-0 bg-gradient-to-bl from-red-600/20 via-transparent to-black/30'></div>
										<div className='absolute top-8 left-8'>
											<div className='w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center'>
												<Eye className='w-8 h-8 text-white' />
											</div>
										</div>
									</div>
								</div>

								<div className='w-full xl:w-1/2'>
									<div className='text-center xl:text-left'>
										<div className='inline-flex items-center gap-3 bg-gray-50 px-6 py-3 rounded-full mb-8'>
											<Eye className='w-6 h-6 text-gray-700' />
											<span className='text-gray-700 font-bold text-lg'>
												Nuestra Visión
											</span>
										</div>

										<h2 className='text-3xl md:text-4xl xl:text-5xl font-black text-gray-900 leading-tight mb-8 tracking-tight'>
											Ser la comunidad líder en
											<span className='text-red-600 block'>
												transformación fitness
											</span>
										</h2>

										<div className='space-y-6 text-gray-700 text-base md:text-lg xl:text-xl font-medium leading-relaxed'>
											<p className='text-gray-800'>
												Aspiramos a ser la comunidad de bienestar más influyente
												y confiable de Latinoamérica, donde cada persona
												encuentre las herramientas, el conocimiento y la
												motivación necesaria para su transformación.
											</p>
											<p>
												Visualizamos un futuro donde el bienestar integral sea
												accesible para todos, creando un impacto positivo que
												trascienda fronteras y genere bienestar sostenible.
											</p>
										</div>

										<div className='mt-10 grid grid-cols-2 gap-4'>
											<div className='text-center p-4'>
												<div className='text-2xl font-black text-red-600 mb-2'>
													2030
												</div>
												<div className='text-sm text-gray-600 font-semibold'>
													Meta de expansión
												</div>
											</div>
											<div className='text-center p-4'>
												<div className='text-2xl font-black text-red-600 mb-2'>
													50K+
												</div>
												<div className='text-sm text-gray-600 font-semibold'>
													Comunidad objetivo
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>

					<section className='py-24 w-full px-6 bg-gradient-to-b from-white to-gray-50/50'>
						<div
							className={`transform transition-all duration-1200 ease-out ${
								isVisible
									? 'translate-y-0 opacity-100'
									: 'translate-y-16 opacity-0'
							}`}
						>
							<div className='text-center mb-20'>
								<h2 className='text-3xl md:text-4xl xl:text-5xl font-black text-gray-900 mb-6 tracking-tight'>
									Números que nos respaldan
								</h2>
								<div className='w-28 h-1.5 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full'></div>
							</div>

							<div className='flex flex-col md:flex-row justify-center items-stretch gap-8 max-w-7xl mx-auto'>
								{metrics.map((metric, index) => (
									<div
										key={index}
										className={`flex-1 max-w-sm mx-auto bg-white p-10 rounded-3xl border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-700 transform hover:-translate-y-3 group backdrop-blur-sm ${
											isVisible
												? 'translate-y-0 opacity-100'
												: 'translate-y-16 opacity-0'
										}`}
										style={{ transitionDelay: `${index * 200 + 400}ms` }}
									>
										<div className='flex flex-col items-center text-center'>
											<div className='w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center mb-8 group-hover:bg-gradient-to-br group-hover:from-red-50 group-hover:to-red-100 transition-all duration-500 shadow-inner'>
												<div className='w-20 h-20 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-500 shadow-lg'>
													{metric.icon}
												</div>
											</div>
											<h3 className='text-4xl xl:text-5xl font-black text-gray-900 mb-3 group-hover:text-red-600 transition-colors duration-500 tracking-tight'>
												{metric.number}
											</h3>
											<p className='text-gray-600 font-bold text-lg xl:text-xl leading-tight'>
												{metric.label}
											</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</section>

					<section className='py-24 max-full px-6'>
						<div className='bg-gradient-to-br w-full from-red-600 via-red-600 to-red-700 rounded-3xl p-16 text-center text-white shadow-2xl relative overflow-hidden'>
							<div className='relative z-10 w-full'>
								<h2 className='text-3xl md:text-4xl xl:text-5xl font-black mb-8 tracking-tight'>
									¿Listo para transformar tu vida?
								</h2>
								<p className='text-xl xl:text-2xl mb-12 opacity-95 max-w-3xl mx-auto font-medium leading-relaxed'>
									Únete a miles de personas que ya han logrado sus objetivos con
									SportCenter Shop
								</p>
								<div className='flex flex-col sm:flex-row gap-6 justify-center items-center'>
									<Link
										to='/'
										className='cursor-pointer bg-white text-red-600 hover:bg-gray-50 px-12 py-4 rounded-full font-black text-lg xl:text-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl'
									>
										Explorar Productos
									</Link>
								</div>
							</div>
						</div>
					</section>
				</div>
			</div>
		</Layout>
	)
}

export default AboutUs
